// Presentation only: never change app price text, variant, quantity, plan or cart.
export function formatUnitLabel(label, style = 'text') {
  if (style === 'none') return '';
  const text = String(label ?? 'per pack').trim();
  if (style !== 'slash') return text;
  const unit = text.replace(/^(?:per\s+|\/\s*)/i, '').trim();
  return unit ? '/' + unit : '';
}

export function dividePrice(text, count) {
  if (!Number.isSafeInteger(count) || count < 1 || count > 10000) return null;
  const source = String(text).trim();
  const match = source.match(/\d(?:[\d.,'’\s\u00a0\u202f]*\d)?/);
  if (!match || /\d/.test(source.slice(match.index + match[0].length))) return null;
  const number = match[0];
  const decimalMatch = number.match(/([.,])(\d{2})$/);
  const decimal = decimalMatch ? decimalMatch[1] : '';
  const whole = decimal ? number.slice(0, -3) : number;
  const group = whole.match(/[^\d]/)?.[0] || (decimal === ',' ? '.' : ',');
  const digits = whole.replace(/\D/g, '');
  const cents = Number(digits) * 100 + (decimalMatch ? Number(decimalMatch[2]) : 0);
  if (!Number.isSafeInteger(cents) || cents < 0) return null;
  const precision = decimal ? 2 : 0;
  const rounded = Math.round(cents / count / (precision ? 1 : 100));
  const major = String(precision ? Math.floor(rounded / 100) : rounded);
  const grouped = major.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  const formatted = grouped + (precision ? decimal + String(rounded % 100).padStart(2, '0') : '');
  return source.slice(0, match.index) + formatted + source.slice(match.index + number.length);
}

// Rupiah display only: compact K/M notation without an approximation symbol.
// Checkout/ATC always retain the unabridged original app text.
export function compactRupiah(text) {
  const match = String(text).match(/^Rp\s*([\d.]+)(?:,(\d{2}))?$/);
  if (!match) return text;
  const amount = Number(match[1].replace(/\./g, '')) + Number(match[2] || 0) / 100;
  if (!Number.isFinite(amount) || amount < 1000) return text;
  const scale = amount >= 1000000 ? 1000000 : 1000;
  const scaled = Math.round(amount / scale * 100) / 100;
  return 'Rp ' + scaled.toFixed(2).replace(/\.?0+$/, '') + (scale === 1000 ? 'K' : 'M');
}

if (typeof customElements !== 'undefined' && !customElements.get('ark-purchase-unit-price')) {
  customElements.define('ark-purchase-unit-price', class extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.section = document.getElementById(this.dataset.section);
      this.content = this.section?.querySelector('.main-product__content');
      if (!this.content) return;
      try { this.config = JSON.parse(this.querySelector('script').textContent); }
      catch { return; }
      this.entries = new Map();
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (this.isConnected) this.sync();
        });
      };
      this.observer = new MutationObserver(records => {
        if (records.some(record => this.isPriceMutation(record))) this.schedule();
      });
      this.observe();
      this.section.addEventListener('change', this.schedule, true);
      this.section.addEventListener('variant:change', this.schedule, true);
      this.onUserChoice = event => {
        if (event.isTrusted && event.target.closest('product-tiers, .product-detail__variant-picker, kaching-subscriptions-block, [data-ark-swatch]')) this.defaultHandled = true;
      };
      this.section.addEventListener('click', this.onUserChoice, true);
      this.section.addEventListener('change', this.onUserChoice, true);
      this.resize = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width !== this.lastWidth) { this.lastWidth = width; this.schedule(); }
      });
      this.resize.observe(this.content);
      this.schedule();
    }
    isPriceMutation(record) {
      const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (target?.closest('.ark-unit-price-line')) return false;
      if (target?.closest('kaching-subscriptions-block')) return true;
      return record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(node =>
        node.nodeType === 1 && (node.matches('kaching-subscriptions-block') || node.querySelector('kaching-subscriptions-block')));
    }
    observe() {
      this.observer.observe(this.content, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['current-variant-id', 'quantity']
      });
    }
    restore(source, line) {
      source.classList.remove('ark-unit-price-source');
      line.remove();
      this.entries.delete(source);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.section?.removeEventListener('change', this.schedule, true);
      this.section?.removeEventListener('variant:change', this.schedule, true);
      this.section?.removeEventListener('click', this.onUserChoice, true);
      this.section?.removeEventListener('change', this.onUserChoice, true);
      this.entries?.forEach((line, source) => this.restore(source, line));
      this.observer = null;
    }
    sync() {
      this.observer.disconnect(); // Own display writes never cause another observer pass.
      try {
        this.entries.forEach((line, source) => {
          if (!this.content.contains(source)) this.restore(source, line);
        });
        const widget = this.content.querySelector('kaching-subscriptions-block');
        if (!widget) return;
        if (this.applyInitialBundle(widget)) return;
        const variantId = widget.getAttribute('current-variant-id');
        const count = Number(this.config.counts[variantId]);
        // Native widget quantity includes cart multiplication, not the free-item count.
        const quantity = Number(widget.getAttribute('quantity') || 1);
        const divisor = count * quantity;
        const groups = [
          { selector: '.ks-price-wrapper', price: '.ks-discounted-price', compare: '.ks-original-price' },
          { selector: '.one-time-purchase .kaching-subscriptions__card-price-wrapper', price: '.kaching-subscriptions__card-price', compare: '.kaching-subscriptions__card-compare-at-price' }
        ];
        groups.forEach(group => widget.querySelectorAll(group.selector).forEach(source => {
          const total = source.querySelector(group.price)?.textContent;
          const reference = source.querySelector(group.compare)?.textContent;
          const price = total && dividePrice(total, divisor);
          // Melts retail is server-derived in presentment currency, per physical pack.
          // Quantity changes the native payable total, not this per-pack reference.
          const retailUnit = this.config.retailUnitReferences?.[variantId];
          const retailCompare = retailUnit?.count === count && typeof retailUnit.money === 'string'
            ? retailUnit.money : '';
          const compare = retailCompare || (reference && dividePrice(reference, divisor));
          if (!price || (reference && !compare)) {
            if (this.entries.has(source)) this.restore(source, this.entries.get(source));
            return; // Invalid settings/unknown format: leave the truthful native total.
          }
          let line = this.entries.get(source);
          if (!line) {
            line = document.createElement('span');
            line.className = 'ark-unit-price-line';
            ['compare', 'value', 'suffix'].forEach(name => {
              const part = document.createElement('span');
              part.className = 'ark-unit-price-' + name;
              line.append(part);
            });
            this.entries.set(source, line);
          }
          const values = [compactRupiah(compare || ''), compactRupiah(price), formatUnitLabel(this.config.label, this.config.labelStyle)];
          [...line.children].forEach((part, index) => {
            if (part.textContent !== values[index]) part.textContent = values[index];
            if (part.hidden !== !values[index]) part.hidden = !values[index];
          });
          if (source.nextElementSibling !== line) source.after(line);
          if (!source.classList.contains('ark-unit-price-source')) source.classList.add('ark-unit-price-source');
          this.fit(line);
        }));
      } finally { if (this.isConnected) this.observe(); }
    }
    applyInitialBundle(widget) {
      if (this.defaultHandled) return false;
      const id = this.config.defaultVariantId;
      if (!id) { this.defaultHandled = true; return false; }
      // Wait for native hydration, using the existing observer, not a timer.
      if (!widget.querySelector('.one-time-purchase .kaching-subscriptions__card-price') || !widget.querySelector('.ks-discounted-price')) return false;
      const radio = [...this.content.querySelectorAll('product-tiers input[data-variant-id]')].find(input => input.dataset.variantId === id);
      this.defaultHandled = true; // Set before dispatch: never reset a shopper's later choice.
      if (!radio || radio.disabled || radio.checked) return false;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.schedule();
      return true;
    }
    fit(line) {
      const fitKey = line.clientWidth + '|' + window.innerWidth + '|' + line.textContent;
      if (line.dataset.fitKey === fitKey) return;
      line.dataset.fitKey = fitKey;
      line.style.removeProperty('font-size');
      line.style.removeProperty('transform');
      line.classList.remove('is-stacked');
      const base = parseFloat(getComputedStyle(line).fontSize);
      const available = line.clientWidth;
      if (!available) return;
      const measure = () => {
        const visible = [...line.children].filter(el => !el.hidden);
        const gap = parseFloat(getComputedStyle(line).columnGap) || 0;
        if (line.classList.contains('is-stacked')) {
          const [compare, price, suffix] = line.children;
          return Math.max(compare.getBoundingClientRect().width,
            price.getBoundingClientRect().width + (suffix.hidden ? 0 : suffix.getBoundingClientRect().width + gap));
        }
        return visible.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0)
          + Math.max(0, visible.length - 1) * gap;
      };
      // Retain both currency prefixes. Move the comparison to its own row
      // before shrinking; the payable price and unit always stay together.
      if (measure() > available && !line.firstElementChild.hidden) {
        line.classList.add('is-stacked');
      }
      const needed = measure();
      if (needed > available) {
        line.style.fontSize = Math.max(base - 2, base * available / needed - .2) + 'px';
      }
      const reduced = measure();
      if (reduced > available) {
        const current = parseFloat(getComputedStyle(line).fontSize);
        line.style.fontSize = Math.max(11, current * available / reduced - .2) + 'px';
      }
      // Last resort for unusually narrow cards: retain both prices and readable
      // text height, fitting their horizontal footprint instead of hiding either.
      const finalWidth = measure();
      if (finalWidth > available) {
        line.style.transform = 'scaleX(' + (available / finalWidth) + ')';
      }
    }
  });
}
